import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Helmet } from 'react-helmet-async';
import {
  AccountTreeOutlined,
  LocalShippingOutlined,
  StorefrontOutlined,
  ShieldOutlined,
} from '@mui/icons-material';

import API from '../data/api';
import LoginForm from '../sections/auth/login/LoginForm';
import ForgotPassword from '../sections/auth/login/ForgotPassword';

const INFO_CARDS = [
  {
    title: 'Unified Fuel Supply Chain',
    description:
      'HoneyBadger connects supplier, logistics, customs, finance, and operations in one workflow.',
    icon: AccountTreeOutlined,
    accent: '#38bdf8',
  },
  {
    title: 'Real-Time Logistics Visibility',
    description:
      'Track order movement from approval to delivery with document and status checkpoints.',
    icon: LocalShippingOutlined,
    accent: '#f59e0b',
  },
  {
    title: 'Audit-Ready Operations',
    description:
      'Structured approvals and evidence capture improve compliance and reduce process risk.',
    icon: ShieldOutlined,
    accent: '#34d399',
  },
];

const WORKFLOW_STEPS = [
  'Order Intake',
  'Allocation',
  'Dispatch',
  'Delivery',
  'Reconciliation',
];

const BACKGROUND_POSTER_URL = '/assets/background.webp';

export default function LoginPage() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [backgroundVideoUrl, setBackgroundVideoUrl] = React.useState('');

  React.useEffect(() => {
    let isMounted = true;

    const fetchBackgroundVideo = async () => {
      try {
        const response = await API.get('leads/background-video');
        const videoUrl = response?.data?.video_url;

        if (isMounted && videoUrl) {
          setBackgroundVideoUrl(videoUrl);
        }
      } catch (error) {
        console.error('Failed to load login background video:', error);
      }
    };

    fetchBackgroundVideo();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleClickForgetPassword = () => {
    setOpen(!open);
  };

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>

      <ForgotPassword open={open} handleClose={handleClickForgetPassword} />

      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
          overflow: 'hidden',
          backgroundImage: `url('${BACKGROUND_POSTER_URL}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          px: 2,
          py: { xs: 3, md: 6 },
        }}
      >
        {backgroundVideoUrl && (
          <Box
            key={backgroundVideoUrl}
            component="video"
            autoPlay
            muted
            loop
            playsInline
            poster={BACKGROUND_POSTER_URL}
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
            }}
          >
            <source src={backgroundVideoUrl} />
          </Box>
        )}

        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background:
              'linear-gradient(105deg, rgba(3, 16, 31, 0.9) 0%, rgba(6, 31, 53, 0.76) 50%, rgba(8, 24, 44, 0.9) 100%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={3} alignItems="stretch">
            <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 2, md: 1 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.18)',
                  background:
                    'linear-gradient(160deg, rgba(11,27,46,0.86) 0%, rgba(8,21,35,0.82) 100%)',
                  backdropFilter: 'blur(8px)',
                  color: '#E5F4FF',
                }}
              >
                <Box
                  component="img"
                  alt="HoneyBadger"
                  src="/assets/images/hb-wordmark-light.png"
                  sx={{ height: 44, mb: 2 }}
                />

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    mb: 1.5,
                    fontFamily: '"Rajdhani", "IBM Plex Sans", sans-serif',
                  }}
                >
                  HoneyBadger ERP + TMS
                </Typography>
                <Typography sx={{ color: 'rgba(229,244,255,0.86)', mb: 3 }}>
                  A digital operating platform for fuel supply and logistics execution, built for
                  operational clarity across Southern African workflows.
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
                  {WORKFLOW_STEPS.map((step) => (
                    <Chip
                      key={step}
                      label={step}
                      size="small"
                      sx={{
                        color: '#D4EBFF',
                        backgroundColor: 'rgba(73, 148, 206, 0.2)',
                        border: '1px solid rgba(122, 180, 224, 0.34)',
                      }}
                    />
                  ))}
                </Stack>

                <Divider sx={{ borderColor: 'rgba(170,210,240,0.2)', mb: 2.5 }} />

                <Grid container spacing={2}>
                  {INFO_CARDS.map((card) => {
                    const Icon = card.icon;
                    return (
                      <Grid size={{ xs: 12 }} key={card.title}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderLeft: `4px solid ${card.accent}`,
                            backgroundColor: 'rgba(255,255,255,0.06)',
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.75 }}>
                            <Icon sx={{ color: card.accent }} />
                            <Typography sx={{ fontWeight: 600 }}>{card.title}</Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'rgba(229,244,255,0.82)' }}>
                            {card.description}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 1, md: 2 } }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.3)',
                  backgroundColor: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Stack spacing={2}>
                  <Box
                    component="img"
                    alt="Honey Badger"
                    src="/assets/images/hb-logo-only-dark.png"
                    sx={{ width: 82, height: 82, alignSelf: 'center' }}
                  />
                  <Typography
                    component="h1"
                    variant="h4"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 700,
                      fontFamily: '"Rajdhani", "IBM Plex Sans", sans-serif',
                    }}
                  >
                    Log in to your account
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    Access operations, documentation, and live logistics workflows.
                  </Typography>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<StorefrontOutlined />}
                    onClick={() => {
                      navigate('/honeybadger-marketplace');
                    }}
                    sx={{
                      borderColor: '#0f766e',
                      color: '#0f766e',
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: '#0b5f59',
                        backgroundColor: 'rgba(15, 118, 110, 0.08)',
                      },
                    }}
                  >
                    HoneyBadger Marketplace
                  </Button>

                  <LoginForm />

                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                    <Typography sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                      Don&apos;t have an account?{' '}
                      <Link
                        sx={{ cursor: 'pointer' }}
                        variant="body2"
                        onClick={() => {
                          navigate('/register');
                        }}
                      >
                        Register
                      </Link>
                    </Typography>
                    <Typography sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Link
                        sx={{ cursor: 'pointer' }}
                        variant="body2"
                        onClick={handleClickForgetPassword}
                      >
                        Forgot Password
                      </Link>
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
